import React, { useLayoutEffect, useRef, useState } from 'react';
import { Button, PopoverGeneral, SparkIcon, TagFeedback, Text, useOutsideClick } from '@spark';
import UpdateAvailableIcon from '@/shared/assets/platform-upgrader/update-available.svg?react';
import { waitingSentence } from '../utils/busy';
import { formatAgo, formatShortScheduledAt } from '../utils/schedule';

const BELL_SELECTOR = '.spark-navigation [aria-label="Notifications"]';
/** With ``tipAlign="end"`` the tip's center sits this far from the panel's right edge. */
const TIP_INSET_PX = 24;
const DURATION = 'Takes up to 10 minutes.';

function Row({ icon, title, tag, tagVariant, children, meta, actions }) {
    return (
        <li className="platform-upgrader-row">
            <span className="platform-upgrader-row-icon" aria-hidden="true">
                {icon}
            </span>
            <div className="platform-upgrader-row-body">
                <div className="platform-upgrader-row-title">
                    <Text as="span" variant="lead-medium" color="primary">
                        {title}
                    </Text>
                    {tag && <TagFeedback label={tag} variant={tagVariant} />}
                </div>
                {children}
                {meta && (
                    <Text as="span" variant="detail-regular" color="primary">
                        {meta}
                    </Text>
                )}
                {actions && <div className="platform-upgrader-row-actions">{actions}</div>}
            </div>
        </li>
    );
}

function Description({ children }) {
    return (
        <Text as="p" variant="base-regular" color="secondary" className="platform-upgrader-paragraph">
            {children}
        </Text>
    );
}

function ActionButtons({ secondary, primary }) {
    return (
        <>
            <Button variant="ghost" size="small" type="button" onClick={secondary.onClick}>
                {secondary.label}
            </Button>
            <Button variant="primary" size="small" type="button" onClick={primary.onClick}>
                {primary.label}
            </Button>
        </>
    );
}

function OfferRow({ upgrader }) {
    const { offer, offeredAt, schedule, isAdmin, inAppUpgrade, upgrade, draining } = upgrader;
    if (draining) {
        return (
            <Row
                icon={<SparkIcon slug="clock-hour-icon" size="medium" />}
                title="Waiting to update"
                tag="Read only"
                tagVariant="warning"
                actions={
                    isAdmin && (
                        <ActionButtons
                            secondary={{ label: 'Cancel update', onClick: upgrader.cancelUpdate }}
                            primary={{ label: 'See processes', onClick: upgrader.showWaitingFor }}
                        />
                    )
                }
            >
                <Description>{waitingSentence(upgrade.waiting_for)}</Description>
            </Row>
        );
    }
    if (upgrade) {
        return (
            <Row
                icon={<SparkIcon slug="refresh" size="medium" />}
                title="Update in progress"
                tag="Read only"
                tagVariant="warning"
                meta={`${upgrade.progress}%`}
            >
                <Description>You can browse the platform, but not make changes.</Description>
            </Row>
        );
    }
    if (schedule) {
        return (
            <Row
                icon={<SparkIcon slug="circle-info" size="medium" />}
                title="Update scheduled"
                tag="Scheduled"
                tagVariant="processing"
                actions={
                    isAdmin && (
                        <ActionButtons
                            secondary={{ label: 'Cancel', onClick: upgrader.cancelSchedule }}
                            primary={{ label: 'Reschedule', onClick: upgrader.openSchedule }}
                        />
                    )
                }
            >
                <Description>{`${formatShortScheduledAt(schedule.at)}. ${DURATION}`}</Description>
            </Row>
        );
    }
    const notifyOnly = isAdmin && !inAppUpgrade;
    return (
        <Row
            icon={<UpdateAvailableIcon width={24} height={24} />}
            title="Update available"
            meta={offeredAt ? formatAgo(offeredAt) : null}
            actions={
                isAdmin &&
                inAppUpgrade && (
                    <ActionButtons
                        secondary={{ label: 'Schedule', onClick: upgrader.openSchedule }}
                        primary={{ label: 'Update now', onClick: upgrader.startUpdateFlow }}
                    />
                )
            }
        >
            <Description>{`The latest version is ready to install. ${DURATION}`}</Description>
            {!isAdmin && <Description>Speak to your admin about updating.</Description>}
            {notifyOnly && <Description>To install it, run helm upgrade with the new chart version.</Description>}
            {notifyOnly && offer.changelog_url && (
                <Description>
                    <a className="platform-upgrader-link" href={offer.changelog_url} target="_blank" rel="noreferrer">
                        See what&apos;s changed
                    </a>
                </Description>
            )}
        </Row>
    );
}

/**
 * The bell's notification center (Figma 91:16636, and 91:13919 for the update
 * on offer): the last completed update and the update on offer or scheduled,
 * with the same actions as the popup.
 */
export default function NotificationPanel({ upgrader }) {
    const panelRef = useRef(null);
    const bellRef = useRef(null);
    const [position, setPosition] = useState(null);

    useLayoutEffect(() => {
        if (!upgrader.panelOpen) return;
        const bell = document.querySelector(BELL_SELECTOR);
        bellRef.current = bell;
        if (!bell) return;
        const rect = bell.getBoundingClientRect();
        setPosition({
            top: rect.bottom + 4,
            right: Math.max(8, window.innerWidth - (rect.left + rect.width / 2) - TIP_INSET_PX),
        });
    }, [upgrader.panelOpen]);

    useOutsideClick([panelRef, bellRef], upgrader.closePanel, upgrader.panelOpen);

    if (!upgrader.panelOpen) return null;
    const { lastUpgrade, offer } = upgrader;

    return (
        <div ref={panelRef} className="platform-upgrader-panel" style={position || undefined}>
            <PopoverGeneral
                tipDirection="top"
                tipAlign="end"
                title="Notifications"
                showClose={false}
                footer={false}
                onMinimizeClick={upgrader.closePanel}
            >
                <div className="platform-upgrader-panel-list">
                    <Text as="span" variant="detail-regular" color="primary" className="platform-upgrader-panel-section">
                        SYSTEM
                    </Text>
                    {!lastUpgrade && !offer ? (
                        <Text as="p" variant="base-regular" color="secondary" className="platform-upgrader-panel-empty">
                            No notifications.
                        </Text>
                    ) : (
                        <ul className="platform-upgrader-rows">
                            {lastUpgrade && (
                                <Row
                                    icon={<SparkIcon slug="circle-check" size="medium" />}
                                    title="Platform updated"
                                    tag="Completed"
                                    tagVariant="success"
                                    meta={formatAgo(lastUpgrade.at)}
                                >
                                    <Description>
                                        {offer
                                            ? 'The last update finished successfully.'
                                            : 'You’re on the latest version.'}
                                    </Description>
                                </Row>
                            )}
                            {offer && <OfferRow upgrader={upgrader} />}
                        </ul>
                    )}
                </div>
            </PopoverGeneral>
        </div>
    );
}
